import { useCallback, useState } from "react";
import { API_URL } from "@/lib/api";

/**
 * Result of a quota check API call
 */
interface QuotaCheckResult {
  quotaFull: boolean;
  segment: string | null;
  error?: string;            // Error message (4xx responses)
  shouldTerminate?: boolean; // Signal to terminate (survey closed, not found)
  // Note: currentCount/quotaMax/groupId are NOT returned (security)
}

/**
 * Return type of the useQuotaCheck hook
 */
interface UseQuotaCheckResult {
  checkQuota: (
    shareToken: string,
    questionId: string,
    answers: Record<string, unknown>,
    cumulative?: boolean
  ) => Promise<QuotaCheckResult>;
  isChecking: boolean;
  error: string | null;
}

/**
 * Hook for real-time quota checking during survey flow.
 * 
 * Checks if a respondent's answers match a segmentation rule
 * and whether that segment's quota is full.
 * 
 * Features:
 * - Fail-open on errors (continues survey if API fails)
 * - Rate limit handling (429 → fail-open)
 * - 4xx handling (survey closed → terminate signal)
 * - 5xx/network handling (fail-open)
 */
export function useQuotaCheck(): UseQuotaCheckResult {
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkQuota = useCallback(async (
    shareToken: string,
    questionId: string,
    answers: Record<string, unknown>,
    cumulative: boolean = false
  ): Promise<QuotaCheckResult> => {
    setIsChecking(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/public/surveys/${shareToken}/check-quota`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ questionId, answers, cumulative }),
        }
      );
      
      // Handle rate limiting (429) - fail-open
      if (response.status === 429) {
        console.warn("Quota check rate limited, failing open");
        return { quotaFull: false, segment: null };
      }
      
      // Handle 4xx errors - signal termination for survey closed/not found
      if (response.status >= 400 && response.status < 500) {
        const data = await response.json().catch(() => ({}));
        return { 
          quotaFull: false, 
          segment: null, 
          error: data.error || "Survey unavailable",
          shouldTerminate: true 
        };
      }
      
      // Handle 5xx errors - fail-open
      if (!response.ok) {
        console.error("Quota check server error, failing open");
        return { quotaFull: false, segment: null };
      }
      
      return await response.json();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Quota check failed";
      setError(message);
      // Network errors - fail-open for better UX
      return { quotaFull: false, segment: null };
    } finally {
      setIsChecking(false);
    }
  }, []);

  return { checkQuota, isChecking, error };
}
